<template>
  <div>
    <div class="container-fluid">
      <!-- ============================================================== -->
      <!-- Bread crumb and right sidebar toggle -->
      <!-- ============================================================== -->
      <div class="row page-titles">
        <div class="col-md-5 col-8 align-self-center">
          <ol class="breadcrumb">
            <li class="breadcrumb-item">
              <router-link to="/home">Home</router-link>
            </li>
            <li class="breadcrumb-item">
              <router-link to="/instruction">申し送りテンプレート一覧</router-link>
            </li>
            <li class="breadcrumb-item active">申し送りテンプレート</li>
          </ol>
        </div>
      </div>
      <div class="row" v-show="errors.any()">
        <div class="col-md-12">
          <div class="alert alert-warning">
            <button type="button" class="close" data-dismiss="alert" aria-label="Close">
              <span aria-hidden="true">×</span>
            </button>
            <ul>
              <li v-for="(error,index) in errors.collect()" :key="index">
                {{error[0]}}
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <div class="card card-outline-info">
            <div class="card-header">
              <h4 class="m-b-0 text-white">申し送り</h4>
            </div>
            <div class="card-body">
              <form action="#" class="form" :disabled="!isLoaded">
                <div class="form-body">
                  <template>
                    <!-- row -->
                    <div class="row">
                      <div class="col-md-3">
                        <div class="form-group" :class="{'has-danger': errors.has('display_name') }">
                          <label class="control-label">テンプレート名</label>
                          <input type="text" name="display_name" class="form-control" maxlength="30"
                                 v-validate="'required|max:30'"
                                 data-vv-as="テンプレート名"
                                 data-vv-validate-on="none"
                                 v-model="inputs.display_name">
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group" :class="{'has-danger': errors.has('contents') }">
                          <label class="control-label">申し送り(100文字制限)</label>
                          <textarea class="form-control" rows="5" v-model="inputs.contents"
                                    data-vv-as="申し送り"
                                    v-validate="'required'"></textarea>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
                <div class="form-actions">
                  <div class="row">
                    <div class="col-md-12 text-center">
                      <router-link to="/instruction">キャンセル</router-link>
                      <span class="mx-2"></span>
                      <button type="button" class="action-button btn btn-danger btn-rounded"
                              @click.prevent="remove"
                              :disabled="!isLoaded"
                              v-if="id && !inputs.builtin_flag">
                        削除する
                      </button>
                      <button type="submit" class="action-button btn btn-success btn-rounded" @click.prevent="save"
                              :disabled="!isLoaded">
                        保存する
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
    <loading-modal v-model="isProcessing" message="更新中..."/>
    <div v-if="showConfirmModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>確認</h3>
        </div>
        <div class="modal-body">
          <p>削除してもよろしいですか？</p>
        </div>
        <div class="modal-buttons">
          <button class="btn btn-cancel" @click="showConfirmModal = false">キャンセル</button>
          <button class="btn btn-confirm" @click="handleConfirm">はい</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import api from '../../libs/Api'
import LoadingModal from '../../components/LoadingModal'
import { reactive, ref, onMounted, toRefs } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import * as yup from 'yup'
import { useExecutor } from '@/libs/Executor'

const { exec } = useExecutor()

const route = useRoute()
const router = useRouter()
const toast = useToast()

const isLoaded = ref(false)
const isProcessing = ref(false)
const showConfirmModal = ref(false)
const errorsValue = ref({})
const errorList = ref([])

const state = reactive({
  id: null,
  inputs: {
    display_name: '',
    contents: ''
  }
})

const { id, inputs } = toRefs(state)

const validationSchema = yup.object().shape({
  display_name: yup.string().trim().required('テンプレート名を入力してください。'),
  contents: yup.string().trim().required('申し送りを入力してください。')
})

const validate = async () => {
  try {
    await validationSchema.validate(state.inputs, { abortEarly: false })

    errorsValue.value = {}
    errorList.value = []

    return true
  } catch (validationError) {
    errorsValue.value = {}

    if (validationError.inner) {
      validationError.inner.forEach(error => {
        errorsValue.value[error.path] = error.message
      })
    }

    errorList.value = []
    for (const key in errorsValue.value) {
      errorList.value.push([errorsValue.value[key]])
    }

    return false
  }
}

const load = async () => {
  exec(async () => {
    if (!state.id) {
      isLoaded.value = true
      return
    }

    const instruction = await api.findInstructionTemplate(state.id)
    if (!instruction) {
      isLoaded.value = true
      return
    }

    state.inputs = _convertEntity2Inputs(instruction)
    isLoaded.value = true
  }).catch((error) => {
    console.error(error)
  })
}

const remove = async () => {
  if (state.inputs.builtin_flag) return
  showConfirmModal.value = true
}

const handleConfirm = async () => {
  exec(async () => {
    isProcessing.value = true
    await api.deleteInstructionTemplate(state.id)
  }).then(() => {
    toast.success('削除しました。')
    router.push('/instruction')
  }).catch((error) => {
    console.error(`Error deleting staff: ${error}`)
    toast.error('削除に失敗しました。')
  }).finally(() => {
    isProcessing.value = false
    showConfirmModal.value = false
  })
}

const setErrors = (errorData) => {
  errorsValue.value = errorData
  errorList.value = []
  for (const key in errorsValue.value) {
    errorList.value.push([errorsValue.value[key]])
  }
}

const save = async () => {
  const isValid = await validate()
  if (isValid) {
    exec(async () => {
      isProcessing.value = true
      const params = _convertInputs2Entity(state.inputs)

      let result
      if (state.id) {
        result = await api.putInstructionTemplate(state.id, params)
      } else {
        result = await api.postInstructionTemplate(params)
      }

      return Promise.resolve(result)
    }, setErrors).then((res) => {
      errorsValue.value = {}
      errorList.value = []

      toast.success('保存しました。')
      state.inputs = _convertEntity2Inputs(res)
      state.id = res.id
      router.push(`/instruction/${state.id}`)
    }).catch((error) => {
      console.error(error)
    }).finally(() => {
      isProcessing.value = false
    })
  }
}

onMounted(() => {
  state.id = route.params.id
  load()
})

// Expose to template
const errors = {
  any: () => Object.keys(errorsValue.value).length > 0,
  has: (field) => {
    if (errorsValue.value[field]) return true
  },
  collect: () => errorList.value
}

function _convertEntity2Inputs (entity) {
  return {
    display_name: entity.display_name,
    contents: entity.contents
  }
}

function _convertInputs2Entity (inputs) {
  return {
    display_name: inputs.display_name,
    contents: inputs.contents
  }
}
</script>
<style scoped>
template {
  display: block !important;
}
.action-button {
  padding: 3px;
  margin: 0 8px;
}
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-buttons {
  display: flex !important;
}

.modal-content {
  background: white;
  border-radius: 4px;
  min-width: 400px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  width: 400px;
  padding: 0px !important;
}

.modal-content hr {
  display: block;
  width: 100%;
}

.modal-header {
  padding-bottom: 5px;
  border-bottom: none !important;
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: bold;
}

.modal-body {
  padding-top: 5px;
  padding-bottom: 20px;
  border-bottom: 1px solid #dee2e6 !important;
}

.modal-body p {
  margin: 0;
  font-size: 14px;
}

.modal-buttons button {
  width: 50%;
  padding: 6px 12px;
  font-size: 14px;
  cursor: pointer;
  border: none;
}

.modal-buttons .btn-cancel {
  background: #fff;
  border-right: 1px solid #ddd;
  color: #333;
}

.modal-buttons .btn-confirm {
  background: #fff;
  color: #333;
}
</style>
